/**
 * CONTRACT FILE. Destination: src/render/types.ts
 * Implemented by:
 *   - W1-11a (app-registry-keymap): src/render/frame/FrameScheduler.ts exports `frameScheduler: FrameScheduler`
 *   - W1-15 (timeline-host):        src/render/timeline/TimelineHost.ts exports `createTimelineHost: CreateTimelineHost`
 *   - W2-01a (preview-gl):          src/render/preview/PreviewRenderer.ts exports `createPreviewRenderer: CreatePreviewRenderer`;
 *                                   src/render/preview/textures.ts exports `createTextureProvider: CreateTextureProvider`
 *   - W2-01b (skins-and-builtin-effects): src/render/skin/SkinStore.ts exports `skinStore: SkinStore`;
 *                                   src/render/preview/effects/builtin/index.ts exports `BUILTIN_SHADERS: readonly BuiltinShaderDef[]`
 *                                   (the W0 stub is an empty list: effects then show the badge "not previewed")
 * Render code receives data and injected functions; it never imports state/, persist/, app/ or worker clients.
 *
 * Rendering contracts (ARCHITECTURE.md §7). The gameplay preview is a custom WebGL2 renderer that draws a
 * FrameDrawList exactly as Phira would (straight alpha SRC_ALPHA/ONE_MINUS_SRC_ALPHA, Phira's bucket
 * order, bilinear without mipmaps, MSAA off unless the "MSAA" Phira setting is on). Editor overlays are
 * drawn on a separate Canvas2D layer above the GL canvas, so the GL output is a pure Phira render (reused
 * for Theater and Test play). Timelines are layered Canvas2D views hosted by one TimelineHost that keeps
 * every column on the same vertical time axis.
 */

import type { ComponentType } from 'react';
import type { Disposable, MessageRef, Unsubscribe, Vec2 } from '../core/util/types';
import type { Beat, Sec } from '../core/time/types';
import type { AssetId, BuiltinShader, EffectId, KeyId, LineId, NoteId } from '../core/model/types';
import type { Engine, FrameDrawList, LineStateRt, SkinMetrics, Viewport } from '../core/engine/types';
import type { RespackFiles, RespackImageName } from '../formats/respack/types';

// ---------------------------------------------------------------------------------------------------------------
// Frame scheduling
// ---------------------------------------------------------------------------------------------------------------

export type FramePriority = 'transport' | 'preview' | 'timeline' | 'overlay' | 'idle';

/** FrameScheduler id of the preview panel's frame (W2-02): `scheduler.request(PREVIEW_FRAME_ID)` redraws the preview and its overlays. */
export const PREVIEW_FRAME_ID = 'preview';

/** One requestAnimationFrame loop for the whole app; views redraw only when dirty (always while playing). */
export interface FrameScheduler {
  /** Called every frame while any subscriber requested it (or while playing). */
  add(id: string, priority: FramePriority, cb: (now: number, dtMs: number) => void): Unsubscribe;
  /** Mark a view dirty so it is called on the next frame. */
  request(id: string): void;
  /** Keep calling every frame (playback). */
  setContinuous(on: boolean): void;
}

// ---------------------------------------------------------------------------------------------------------------
// Skins and textures
// ---------------------------------------------------------------------------------------------------------------

export interface LoadedSkin {
  readonly id: string;
  readonly name: string;
  readonly source: 'localPhigros' | 'respack' | 'procedural';
  readonly metrics: SkinMetrics;
  readonly images: Readonly<Record<RespackImageName, ImageBitmap>>;
  /** Hold body slices for holdRepeat (rows A0..H-A1), created at load. */
  readonly holdBody: ImageBitmap | null;
  readonly holdBodyMh: ImageBitmap | null;
  /** Encoded sound bytes (decoded by the audio engine into its HitsoundBank). */
  readonly sounds: Readonly<Record<'click' | 'drag' | 'flick', { readonly bytes: Uint8Array; readonly ext: string } | null>>;
}

export interface SkinSummary {
  readonly id: string;
  readonly name: string;
  readonly author: string;
  readonly source: LoadedSkin['source'];
}

/** Storage for installed respacks, injected by the app wiring (backed by PersistClient list/get/put/deleteSkin). */
export interface SkinStorage {
  list(): Promise<ReadonlyArray<{ readonly id: string; readonly name: string; readonly author: string }>>;
  get(id: string): Promise<RespackFiles>;
  put(pack: RespackFiles): Promise<string>;
  remove(id: string): Promise<void>;
}

/**
 * Default skin resolution: user-selected respack -> local Phigros skin (dev/local builds only, served by
 * the Vite plugin `phiedit-local-skin` from docs/research/raw/skin; never in public builds or git) ->
 * procedural skin (drawn at startup with the Phigros skin's sizes/aspects, synthesised sounds).
 * Before `init` resolves, `active` is the placeholder skin (default SkinMetrics from S§3.6, 1x1 bitmaps).
 */
export interface SkinStore {
  /** Loads the installed list and the default skin (settings.preview.defaultSkin, null = resolution order). */
  init(deps: { readonly storage: SkinStorage; readonly defaultId: string | null }): Promise<void>;
  /** Resolves when the procedural/local/default skin (incl. OfflineAudioContext sounds) is ready. */
  readonly ready: Promise<void>;
  list(): readonly SkinSummary[];
  readonly active: LoadedSkin;
  setActive(id: string): Promise<void>;
  /**
   * Install a respack that formats/respack already validated (the io worker unzips and validates; ImportResult
   * kind 'respack'). Render never unzips. Returns the installed id.
   */
  install(pack: RespackFiles): Promise<{ readonly ok: true; readonly id: string } | { readonly ok: false; readonly errors: readonly MessageRef[] }>;
  remove(id: string): Promise<void>;
  subscribe(cb: (s: LoadedSkin) => void): Unsubscribe;
  /**
   * Why skins were skipped while resolving or loading (local Phigros skin invalid or unreachable, an installed pack
   * damaged in storage, the chosen default unavailable), as MessageRefs (`skin.*`, `respack.*`), newest last.
   */
  problems?(): readonly MessageRef[];
}

export interface DecodedGif {
  readonly frames: readonly ImageBitmap[];
  /** Phira timing: AssetMeta.gifDelaysMs (GCE delay x 10 ms, unclamped), never browser animation timing. */
  readonly delaysMs: readonly number[];
  readonly totalMs: number;
  /** Frame index at position `ms` within the loop (Phira frames_at). */
  frameAt(ms: number): number;
}

/** Project media for the renderer: decoded lazily (ImageDecoder where available, gifuct-js fallback). */
export interface TextureProvider extends Disposable {
  /** Null while loading (a redraw is requested when ready). */
  image(asset: AssetId): ImageBitmap | null;
  gif(asset: AssetId): DecodedGif | null;
  /** Illustration blurred exactly like Phira (3 box passes, width 101, edge-clamped; computed in the io worker). */
  blurredIllustration(asset: AssetId): ImageBitmap | null;
  /** UTF-8 source of a custom shader asset. */
  shaderSource(asset: AssetId): string | null;
  /** Video element for an asset (P1 preview). */
  video(asset: AssetId): HTMLVideoElement | null;
  onReady(cb: () => void): Unsubscribe;
  /**
   * Why a medium failed to load (e.g. a shader file that is not UTF-8, which Phira rejects), or null while loading or
   * after a successful load; `kind` narrows it to one use of the asset.
   */
  failure?(asset: AssetId, kind?: 'image' | 'gif' | 'shader' | 'video' | 'blur'): string | null;
}

export type CreateTextureProvider = (deps: {
  readonly load: (asset: AssetId) => Promise<Uint8Array>;
  /** Phira-exact blur, computed in the io worker (IoWorkerSchema.blurIllustration). */
  readonly blur: (bytes: Uint8Array, maxSide: number) => Promise<ImageBitmap>;
  /** GIF frame delays as Phira reads them (AssetMeta.gifDelaysMs), null when unknown. */
  readonly gifDelays: (asset: AssetId) => readonly number[] | null;
}) => TextureProvider;

/**
 * A built-in extra.json shader, REIMPLEMENTED from its behavioural description (P§5.2; no Phira shader source).
 * Badged "approximate" until verified against Phira captures (human checklist).
 */
export interface BuiltinShaderDef {
  readonly name: BuiltinShader;
  /** GLSL ES 1.00 fragment shader (our own source). */
  readonly fragment: string;
  /** Uniform defaults applied before the effect's vars. */
  readonly defaults: Readonly<Record<string, number | readonly number[]>>;
}

// ---------------------------------------------------------------------------------------------------------------
// Preview renderer (WebGL2)
// ---------------------------------------------------------------------------------------------------------------

export interface RenderOptions {
  readonly msaa: boolean;
  /** Effects disabled (Phira "No Shader" / setting). */
  readonly effects: boolean;
  readonly videos: boolean;
}

export interface RenderStats {
  readonly cpuMs: number;
  readonly drawCalls: number;
  readonly quads: number;
  /**
   * Custom shader compile failures (the preview forwards them to ProblemsService.reportShaderErrors, PH-33); a shader
   * that failed to LOAD is listed with a log starting with `load:`.
   */
  readonly shaderErrors: ReadonlyArray<{ readonly asset: AssetId; readonly log: string }>;
  /** Effects drawn without their shader (a built-in missing from BUILTIN_SHADERS): badge "not previewed". */
  readonly notPreviewedEffects: readonly EffectId[];
  /** Effects skipped because their shader source is still loading. */
  readonly pendingEffects: readonly EffectId[];
  readonly videosDrawn: number;
  /** GPU time of a recent frame (ms) where the browser exposes timer queries, else null. */
  readonly gpuMs: number | null;
  /** Current automatic render-scale factor (1 = full resolution). */
  readonly renderScale: number;
}

/**
 * The WebGL2 preview renderer. The context is always created with `antialias: false`; Phira's MSAA setting renders
 * into a 4x multisampled renderbuffer (resolved before the non-global effects and before presenting).
 */
export interface PreviewRenderer extends Disposable {
  readonly canvas: HTMLCanvasElement;
  /** `dpr`: the caller's cap (renderScale setting or devicePixelRatio); the automatic scale lowers below it. */
  resize(cssW: number, cssH: number, dpr: number): void;
  setSkin(skin: LoadedSkin): void;
  setTextures(t: TextureProvider): void;
  /** Draws one frame. `engine` is used for paint-line stamps (paint buffers are renderer state). */
  render(list: FrameDrawList, engine: Engine, opts: RenderOptions): RenderStats;
  readonly contextLost: boolean;
  onContextRestored(cb: () => void): Unsubscribe;
  /** False when the browser gave no WebGL2 context (the canvas stays black; the panel explains why). */
  readonly available: boolean;
  /** Why the renderer is unavailable (technical text), or null. */
  readonly failure: string | null;
  /**
   * 'auto' (default; settings.preview.renderScale 'auto'): frames over 14 ms for 1 s lower the scale; 'fixed': the
   * resolution stays css x dpr exactly.
   */
  setRenderScaleMode(mode: 'auto' | 'fixed'): void;
  /** Current factor applied on top of the device-pixel ratio. */
  readonly renderScale: number;
  /** HUD text width in UI units with the preview fonts: pass it as FrameOptions.measureText. */
  readonly measureHudText: (text: string, font: 'pgr' | 'ui', size: number) => number;
  /**
   * The last frame is stale and should be drawn again (fonts loaded, media ready, a video seek finished, text deferred
   * by the raster budget, the automatic scale changed). Called once per burst, never from inside `render`.
   */
  onInvalidate(cb: () => void): Unsubscribe;
}

export type CreatePreviewRenderer = (canvas: HTMLCanvasElement) => PreviewRenderer;

// ---------------------------------------------------------------------------------------------------------------
// Preview overlays (Canvas2D layer above the GL canvas; Edit mode only)
// ---------------------------------------------------------------------------------------------------------------

export interface OverlayDrawContext {
  readonly ctx: CanvasRenderingContext2D;
  readonly viewport: Viewport;
  readonly t: Sec;
  readonly list: FrameDrawList;
  readonly lines: readonly LineStateRt[];
  readonly engine: Engine;
  readonly dpr: number;
  /** World (chart space, y up) -> canvas px. */
  worldToPx(x: number, y: number): Vec2;
  pxToWorld(px: number, py: number): Vec2;
  /** UI space (y down) -> canvas px. */
  uiToPx(x: number, y: number): Vec2;
}

export interface OverlayHit {
  readonly provider: string;
  readonly cursor: string;
  readonly data: unknown;
}

export interface OverlayPointerEvent {
  readonly px: number;
  readonly py: number;
  readonly world: Vec2;
  readonly buttons: number;
  readonly shift: boolean;
  readonly ctrl: boolean;
  readonly alt: boolean;
}

/**
 * Contributed by feature modules (FeatureModule.previewOverlays): gizmo, line labels, selection
 * outlines, parent connectors, safe areas, hidden-by-rule ghosts, sample-mode handles, motion-path preview.
 */
export interface PreviewOverlayProvider {
  readonly id: string;
  /** Draw order (higher on top). */
  readonly order: number;
  draw(c: OverlayDrawContext): void;
  hitTest?(c: OverlayDrawContext, px: number, py: number): OverlayHit | null;
  onPointerDown?(c: OverlayDrawContext, hit: OverlayHit, e: OverlayPointerEvent): void;
  onPointerMove?(c: OverlayDrawContext, e: OverlayPointerEvent): void;
  onPointerUp?(c: OverlayDrawContext, e: OverlayPointerEvent): void;
}

// ---------------------------------------------------------------------------------------------------------------
// Timeline host and columns (Canvas2D, time flows UP, shared by lane and motion columns)
// ---------------------------------------------------------------------------------------------------------------

/** Vertical time axis shared by all columns. Beats are f64 here (display only; edits snap to exact Beats). */
export interface TimeAxis {
  readonly pxPerBeat: number;
  readonly bottomBeat: number;
  readonly heightPx: number;
  readonly dpr: number;
  beatAtY(y: number): number;
  yAtBeat(beat: number): number;
  /** Visible beat range [bottom, top]. */
  readonly range: readonly [number, number];
  /** Snap a beat (f64) to the current snap grid as an exact Beat. */
  snap(beat: number): Beat;
}

export type TimelineLayer = 'static' | 'content' | 'overlay';

export interface TimelinePointerEvent {
  readonly x: number;
  readonly y: number;
  readonly beat: number;
  readonly buttons: number;
  readonly shift: boolean;
  readonly ctrl: boolean;
  readonly alt: boolean;
  readonly detail: number;
}

export interface TimelineHit {
  readonly kind: 'note' | 'noteHead' | 'noteTail' | 'key' | 'keyIn' | 'keyOut' | 'segment' | 'clipHandle' | 'bezierHandle' | 'empty' | 'gutter' | 'header';
  readonly note?: NoteId;
  readonly key?: KeyId;
  readonly line?: LineId;
  readonly cursor: string;
}

/** Items inside a marquee rectangle (beats f64, x in column px). */
export interface TimelineRectItems {
  readonly notes?: readonly NoteId[];
  readonly keys?: readonly KeyId[];
  readonly segments?: readonly KeyId[];
}

/** A column's renderer + interaction (lane, waveform, motion property, effect lane...). */
export interface TimelineColumnView extends Disposable {
  /** Called when the column's width or the axis changes. */
  layout(widthPx: number): void;
  /**
   * Draw a layer for the visible range. P0 has no tiles: the host redraws the content layer of the visible range
   * when the axis changed, a ChangeSet arrived or engine.revision changed (views cache sampled curves per
   * (track, engine.revision, range, px step)); the overlay layer every frame while active.
   */
  draw(layer: TimelineLayer, ctx: CanvasRenderingContext2D, axis: TimeAxis, widthPx: number): void;
  hitTest(x: number, y: number, axis: TimeAxis): TimelineHit | null;
  /**
   * Marquee support: the host computes the rectangle across columns and merges every column's answer into the
   * selection. x is column-local; x0 may be -Infinity and x1 +Infinity (a time-range selection of the gutter, the
   * half-open beat range [beat0, beat1)): that side has no horizontal limit, so items outside the column's visible X /
   * value range are included; implementations compare against the bounds directly (never derive NaN from them).
   */
  itemsInRect?(beat0: number, beat1: number, x0: number, x1: number, axis: TimeAxis): TimelineRectItems;
  onPointerDown?(e: TimelinePointerEvent, hit: TimelineHit | null): void;
  onPointerMove?(e: TimelinePointerEvent): void;
  onPointerUp?(e: TimelinePointerEvent): void;
  onDoubleClick?(e: TimelinePointerEvent, hit: TimelineHit | null): void;
  onContextMenu?(e: TimelinePointerEvent, hit: TimelineHit | null): void;
  /**
   * Wheel inside the column (return true when consumed; otherwise the host scrolls/zooms). The host's own scrolling:
   * positive normalised deltaY (wheel down) scrolls LATER in time (like playing, notes move down towards the
   * playhead); one 100 px notch = settings.editing.scrollSensitivity beats.
   */
  onWheel?(e: WheelEvent, axis: TimeAxis): boolean;
  /** Ctrl+Shift+wheel over the column: zoom its horizontal axis by `factor` about column x. */
  zoomX?(factor: number, x: number): void;
  /**
   * Horizontal wheel or middle drag over the column: pan its horizontal axis by dxPx CSS px; positive moves the view
   * right (like scrollLeft += dxPx). Without it the columns area scrolls sideways.
   */
  panX?(dxPx: number): void;
  /** The pointer left the column (end hover feedback). */
  onPointerLeave?(): void;
  /** The view's pointer gesture was cancelled (Esc, pointercancel, lost capture, the column left the layout). */
  onPointerCancel?(): void;
}

export interface TimelineColumnInstance {
  /** Unique instance id, e.g. 'lane:12', 'motion:12:moveX:0', 'waveform'. */
  readonly id: string;
  readonly defId: string;
  readonly widthPx: number;
  readonly header: ComponentType<{ readonly instanceId: string }> | null;
  readonly view: TimelineColumnView;
  /** data-region of the column element (focus / hover atoms, region context menu): 'lane', 'motion', 'curve'... */
  readonly region?: string;
  /** Registry menu (MenuId) the shell opens on a right click in the column. */
  readonly contextMenu?: string;
  /** Accessible name of the column element (default: the def's title), e.g. "Rotation, Main". */
  readonly label?: string;
}

/** Column type contributed by a feature module (FeatureModule.timelineColumns). */
export interface TimelineColumnDef {
  readonly id: string;
  readonly title: MessageRef;
  /** Left-to-right placement: gutter 0, waveform 10, lanes 20, motion 30, effects 40. */
  readonly order: number;
  readonly width: { readonly min: number; readonly default: number; readonly max: number };
  /**
   * Instances to show given the current editor state (e.g. one per pinned lane, one per visible property/layer). The
   * host owns the views it is handed: a view whose instance leaves the layout, or that is replaced by another view
   * object under the same instance id, is disposed (after cancelling its gesture), and all views are disposed with the
   * host. Column defs create a fresh view when an instance (re)appears.
   */
  instances(host: TimelineHostApi): readonly TimelineColumnInstance[];
}

/** Ticks/bands drawn by the host in the gutter and overview strip (problems, markers, BPM changes, loop). */
export interface TimelineDecoration {
  readonly beat: number;
  readonly endBeat?: number;
  readonly kind: 'problem-fatal' | 'problem-error' | 'problem-warning' | 'problem-tip' | 'marker' | 'bpm' | 'loop' | 'generator' | 'selection';
  readonly label?: string;
  readonly onClick?: () => void;
}

export interface TimelineDecorationProvider {
  readonly id: string;
  decorations(range: readonly [number, number]): readonly TimelineDecoration[];
  subscribe(cb: () => void): Unsubscribe;
}

export interface TimelineHostApi {
  readonly axis: TimeAxis;
  /** Mark a column instance's layer (or all) for redraw on the next frame. */
  invalidate(instanceId?: string, layer?: TimelineLayer): void;
  /**
   * Re-query every column def's `instances()` and widths (motion columns appear when showMotionColumns, the active
   * line, layers or pinned columns change; the curve editor widens a column).
   */
  relayout(): void;
  requestFrame(): void;
  /** Scroll so `beat` is visible (jump-to-change, problems navigation). */
  reveal(beat: number, where?: 'center' | 'playhead'): void;
  setPxPerBeat(pxPerBeat: number, anchorBeat?: number): void;
  /**
   * Begin a pointer capture across columns: called during `onPointerDown` (or a later move of the same press) it turns
   * the press into a host-run marquee anchored at the press point. The host draws it over every column it spans, asks
   * each column's `itemsInRect`, merges the answers and reports marquee results (the controller's `marquee` events:
   * update once per frame, end, cancel on Esc; Shift = add, Ctrl/Cmd = toggle, Alt or Ctrl+Shift = subtract, else
   * replace). The owner then gets `onPointerUp` (or `onPointerCancel`). A press on empty space of a column without
   * `onPointerDown` starts a marquee by itself after 4 px; a click there reports an empty 'replace' marquee. Presses
   * go to the column under them; moves and the release go to that column (implicit capture, auto-scroll near edges).
   */
  capturePointer(owner: string): void;
}

/**
 * View configuration pushed by the Chart timeline panel (mirrors the editor store; render/ never imports state/).
 * A two-way binding: the panel mirrors `onViewChange` into its store and sends the store back with `setView`. The
 * host applies the axis fields (pxPerBeat, bottomBeat, snapDiv) of a `setView` only as new intents: a config equal to
 * the host's axis changes nothing; a late echo of a report the host has superseded (within a second) is ignored for
 * its axis fields; otherwise each axis field is applied when it differs from what the panel last saw. While playing
 * and following, `setView` never moves `bottomBeat`. Follow-driven reports are throttled to 4 per second and flushed
 * when playback stops. Commands that move the view on purpose call `reveal`, `frameRange` or `setPxPerBeat`.
 */
export interface TimelineViewConfig {
  readonly pxPerBeat: number;
  readonly bottomBeat: number;
  readonly snapDiv: number;
  readonly beatSnap: boolean;
  /** Playhead position from the bottom as a fraction of the height (settings.editing.playheadPosition, 0.25). */
  readonly playheadRatio: number;
  readonly followPlayhead: boolean;
  /** Editor meter marks (Timing.meter as f64 beats): bar lines, PageUp/PageDown bar seeking, metronome accents. */
  readonly meter: ReadonlyArray<{ readonly beat: number; readonly beatsPerBar: number }>;
  /** Waveform column/backdrop opacity 0..1. */
  readonly waveformOpacity: number;
}

export interface TimelineHost extends Disposable, TimelineHostApi {
  mount(container: HTMLElement): void;
  setColumnDefs(defs: readonly TimelineColumnDef[]): void;
  setDecorations(providers: readonly TimelineDecorationProvider[]): void;
  setView(cfg: TimelineViewConfig): void;
  /** Scroll/zoom changes made by the host (wheel, drag, follow) so the panel can persist them to the editor store. */
  onViewChange(cb: (cfg: TimelineViewConfig) => void): Unsubscribe;
  /**
   * Current playhead beat (f64) for the overlay layer; called every frame while playing. It never scrolls while
   * paused: after a seek that does not come from the timeline itself (Home/End, PageUp/PageDown, markers, the
   * transport's seek bar) the panel calls `reveal(beat)` (scrolls only when off screen). Seeks from the gutter or the
   * overview are handled by the host. The waveform source is installed by the Chart timeline panel (W2-03, from the
   * audio worker's peaks, honouring the `timeline.waveform` toggle).
   */
  setPlayhead(beat: number): void;
}

/**
 * The implementation (src/render/timeline/TimelineHost.ts, `createTimelineHostController`) also takes optional
 * `options` and returns a `TimelineHostController` (settings, events, time range, waveform, overview...): render
 * module code features may import directly (`asTimelineHostController(services.timeline)`).
 */
export type CreateTimelineHost = (deps: { readonly scheduler: FrameScheduler }) => TimelineHost;

/**
 * Shared timeline drawing helpers (W1-15, src/render/timeline/glyphs.ts): key diamonds, half-diamond jump keys,
 * hold glyphs, note glyph shapes, density bands; used by the lane (W2-03) and motion columns (W2-04a/b). Glyphs draw
 * with the caller's fillStyle / strokeStyle / lineWidth and restore every state they change. `jumpKey` draws ONE
 * half-diamond: arriveLeft = true is the arrive half (`<|`, pointing left, flat edge on x), false the leave half
 * (`|>`); a jump key draws both halves at x(vIn) and x(vOut). `holdSegment` is the "Stay, then jump" dashed line.
 * `densityBand`'s density is items per pixel row (opacity grows logarithmically and saturates at 8). glyphs.ts also
 * exports `noteGlyph` / `holdGlyph` / `noteGlyphPath` (tap 8, drag 5, flick 8, hold 8 px thick) and `hatchRect`.
 */
export interface TimelineGlyphs {
  keyDiamond(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, filled: boolean): void;
  jumpKey(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, arriveLeft: boolean): void;
  holdSegment(ctx: CanvasRenderingContext2D, x: number, y0: number, y1: number): void;
  densityBand(ctx: CanvasRenderingContext2D, x0: number, x1: number, y: number, h: number, density: number): void;
}
